import { useCallback, useState } from "react";
import { SelectionDropdown } from "../../../../../../components/Dropdown/SelectionDropdown.tsx";
import { useOnMount } from "../../../../../../hooks/useOnMount.ts";
import {
  rgbaStringToRgba,
  rgbaToString,
} from "../../../../../../utils/colors.ts";
import {
  RECT_SHAPE_TYPE,
  SHAPE_LAYER_TYPE,
  SHAPE_TYPES,
} from "../../Designer/constants.ts";
import { useLayerRotationEventListener } from "../../Designer/hooks/useLayerRotationEventListener.ts";
import { useLayerSelectionEventListener } from "../../Designer/hooks/useLayerSelectionEventListener.ts";
import { designerCanvasStore } from "../../Designer/stores/designerCanvasStore.ts";
import type {
  RectShapeLayer,
  RectShapeLayerExtraSettings,
  ShapeLayer,
  ShapeLayerSettings,
} from "../../Designer/types.ts";
import {
  getSpecificActiveLayers,
  normalizeLayerAngle,
} from "../../Designer/utils/layer.ts";
import {
  updateRectShapeLayer,
  updateShapeLayer,
} from "../../Designer/utils/shape.ts";
import { DesignerColorPickerButton } from "../components/DesignerSettings/DesignerColorPickerButton.tsx";
import { DesignerRotationInputNumber } from "../components/DesignerSettings/DesignerRotationInputNumber.tsx";
import { ShadowSettingsSection } from "../components/DesignerSettings/ShadowSettingsSection.tsx";
import { StrokeSettingsSection } from "../components/DesignerSettings/StrokeSettingsSection.tsx";
import { DesignerSettingsItem } from "../components/Layout/DesignerSettingsItem.tsx";
import { DesignerSettingsLayout } from "../components/Layout/DesignerSettingsLayout.tsx";
import { SETTINGS_ITEM_WIDTH } from "../constants.ts";
import { CornerRadiusInputNumber } from "./components/CornerRadiusInputNumber.tsx";
import { SHAPE_TYPE_DISPLAY_NAMES } from "./display.ts";

export const EditShapeSettingsControls = () => {
  const { selectedShapeLayersSettings, setSelectedShapeLayersSettings } =
    useSelectedShapeLayersSettings();

  const {
    selectedRectShapeLayersExtraSettings,
    setSelectedRectShapeLayersExtraSettings,
  } = useSelectedRectShapeLayersExtraSettings();

  return (
    <DesignerSettingsLayout>
      <DesignerSettingsItem
        title="Shape"
        rightItem={
          <SelectionDropdown
            value={selectedShapeLayersSettings.shapeType}
            onChange={(shapeType) =>
              setSelectedShapeLayersSettings({
                shapeType,
              })
            }
            options={SHAPE_TYPES.map((it) => ({
              value: it,
              label: SHAPE_TYPE_DISPLAY_NAMES[it],
            }))}
            size="sm"
            buttonClassName={SETTINGS_ITEM_WIDTH}
          />
        }
      />
      <DesignerSettingsItem
        title="Color"
        rightItem={
          <DesignerColorPickerButton
            color={rgbaStringToRgba(selectedShapeLayersSettings.fill)}
            onColorChange={(fill) =>
              setSelectedShapeLayersSettings({ fill: rgbaToString(fill) })
            }
          />
        }
      />
      <DesignerSettingsItem
        title="Rotation"
        rightItem={
          <DesignerRotationInputNumber
            rotation={selectedShapeLayersSettings.rotation}
            onRotationChange={(rotation) =>
              setSelectedShapeLayersSettings({ rotation })
            }
          />
        }
      />
      <DesignerSettingsItem title="Stroke">
        <StrokeSettingsSection
          strokeSettings={{
            strokeColor: selectedShapeLayersSettings.stroke,
            strokeStyle: selectedShapeLayersSettings.strokeStyle,
            strokeWidth: selectedShapeLayersSettings.strokeWidth,
          }}
          onStrokeSettingsChange={(strokeSettings) => {
            const { strokeColor, strokeStyle, strokeWidth } = strokeSettings;
            setSelectedShapeLayersSettings({
              stroke: strokeColor ?? selectedShapeLayersSettings.stroke,
              strokeStyle:
                strokeStyle ?? selectedShapeLayersSettings.strokeStyle,
              strokeWidth:
                strokeWidth ?? selectedShapeLayersSettings.strokeWidth,
            });
          }}
          minThickness={0}
          maxThickness={20}
        />
      </DesignerSettingsItem>
      <DesignerSettingsItem title="Shadow">
        <ShadowSettingsSection
          shadowSettings={selectedShapeLayersSettings}
          onShadowSettingsChange={setSelectedShapeLayersSettings}
        />
      </DesignerSettingsItem>
      {selectedShapeLayersSettings.shapeType === RECT_SHAPE_TYPE && (
        <DesignerSettingsItem
          title="Corner radius"
          rightItem={
            <CornerRadiusInputNumber
              radius={selectedRectShapeLayersExtraSettings.cornerRadius}
              onRadiusChange={(radius) =>
                setSelectedRectShapeLayersExtraSettings({
                  cornerRadius: radius,
                })
              }
            />
          }
        />
      )}
    </DesignerSettingsLayout>
  );
};

const useSelectedShapeLayersSettings = () => {
  const { fabricCanvas } = designerCanvasStore.useState();

  const [selectedShapeLayersSettings, setSelectedShapeLayersSettings] =
    useState<Required<ShapeLayerSettings>>({
      fill: "rgba(0, 0, 0, 1)",
      shapeType: RECT_SHAPE_TYPE,
      rotation: 0,
      stroke: "rgba(0, 0, 0, 1)",
      strokeWidth: 0,
      strokeStyle: "solid",
      shadowStyle: "none",
      shadowDirection: "top",
      shadowDistance: "small",
    });

  const updateSelectedShapeLayersSettingsFromShapeLayer = useCallback(
    (shapeLayer: ShapeLayer) => {
      setSelectedShapeLayersSettings({
        fill: shapeLayer.fill as string,
        shapeType: shapeLayer.shapeType,
        rotation: normalizeLayerAngle(shapeLayer.angle),
        stroke: shapeLayer.stroke as string,
        strokeWidth: shapeLayer.strokeWidth,
        strokeStyle: shapeLayer.strokeStyle,
        shadowStyle: shapeLayer.shadowStyle,
        shadowDirection: shapeLayer.shadowDirection,
        shadowDistance: shapeLayer.shadowDistance,
      });
    },
    [],
  );

  useOnMount(() => {
    if (!fabricCanvas) return;
    const activeShapeLayers = getSpecificActiveLayers({
      fabricCanvas,
      layerType: SHAPE_LAYER_TYPE,
    });
    if (activeShapeLayers.length === 0) return;
    updateSelectedShapeLayersSettingsFromShapeLayer(activeShapeLayers[0]);
  });

  useLayerSelectionEventListener({
    fabricCanvas,
    layerType: SHAPE_LAYER_TYPE,
    onLayerSelectionUpdate: updateSelectedShapeLayersSettingsFromShapeLayer,
  });

  useLayerRotationEventListener({
    fabricCanvas,
    layerType: SHAPE_LAYER_TYPE,
    onLayerRotationEvent: (rotation) => {
      setSelectedShapeLayersSettings({
        ...selectedShapeLayersSettings,
        rotation,
      });
    },
  });

  const updateSelectedShapeLayersSettings = useCallback(
    (options: Partial<Required<ShapeLayerSettings>>) => {
      setSelectedShapeLayersSettings({
        ...selectedShapeLayersSettings,
        ...options,
      });
      if (!fabricCanvas) return;
      for (const layer of getSpecificActiveLayers({
        fabricCanvas,
        layerType: SHAPE_LAYER_TYPE,
      })) {
        if (options.shapeType && options.shapeType !== layer.shapeType) {
          designerCanvasStore.convertShapeLayerTo(layer, options.shapeType);
        } else {
          updateShapeLayer(layer, options);
        }
      }
      fabricCanvas.renderAll();
    },
    [selectedShapeLayersSettings, fabricCanvas],
  );

  return {
    selectedShapeLayersSettings,
    setSelectedShapeLayersSettings: updateSelectedShapeLayersSettings,
  };
};

const useSelectedRectShapeLayersExtraSettings = () => {
  const { fabricCanvas } = designerCanvasStore.useState();

  const [
    selectedRectShapeLayersExtraSettings,
    setSelectedRectShapeLayersExtraSettings,
  ] = useState<Required<RectShapeLayerExtraSettings>>({
    cornerRadius: 0,
  });

  const updateSelectedRectShapeLayersExtraSettingsFromRectShapeLayer =
    useCallback((rectShapeLayer: RectShapeLayer) => {
      setSelectedRectShapeLayersExtraSettings({
        cornerRadius: rectShapeLayer.rx,
      });
    }, []);

  useOnMount(() => {
    if (!fabricCanvas) return;
    const activeShapeLayers = getSpecificActiveLayers({
      fabricCanvas,
      layerType: SHAPE_LAYER_TYPE,
    });
    if (activeShapeLayers.length === 0) return;
    const shapeLayer = activeShapeLayers[0];
    if (shapeLayer.shapeType === RECT_SHAPE_TYPE) {
      updateSelectedRectShapeLayersExtraSettingsFromRectShapeLayer(shapeLayer);
    }
  });

  useLayerSelectionEventListener({
    fabricCanvas,
    layerType: SHAPE_LAYER_TYPE,
    onLayerSelectionUpdate: (layer) => {
      if (layer.shapeType === RECT_SHAPE_TYPE) {
        updateSelectedRectShapeLayersExtraSettingsFromRectShapeLayer(layer);
      }
    },
  });

  const updateSelectedRectShapeLayersExtraSettings = useCallback(
    (options: Partial<Required<RectShapeLayerExtraSettings>>) => {
      setSelectedRectShapeLayersExtraSettings({
        ...selectedRectShapeLayersExtraSettings,
        ...options,
      });
      if (!fabricCanvas) return;
      for (const layer of getSpecificActiveLayers({
        fabricCanvas,
        layerType: SHAPE_LAYER_TYPE,
      })) {
        if (layer.shapeType === RECT_SHAPE_TYPE) {
          updateRectShapeLayer(layer, options);
        }
      }
      fabricCanvas.renderAll();
    },
    [selectedRectShapeLayersExtraSettings, fabricCanvas],
  );

  return {
    selectedRectShapeLayersExtraSettings,
    setSelectedRectShapeLayersExtraSettings:
      updateSelectedRectShapeLayersExtraSettings,
  };
};
